import { useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, CheckCircle2 } from "lucide-react";
import { SITE, SERVICES } from "../data/site";
import SectionHeader from "../components/SectionHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [params] = useSearchParams();
  const isQuote = params.get("type") === "quote";
  const [mode, setMode] = useState(isQuote ? "quote" : "contact");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd.entries());
    try {
      if (mode === "quote") {
        await axios.post(`${API}/quotes`, data);
      } else {
        await axios.post(`${API}/contact`, data);
      }
      setSent(true);
      e.target.reset();
    } catch (err) {
      setError("Could not submit, please try again.");
    }
    setSending(false);
  };

  return (
    <section className="container-st pt-16 pb-24">
      <SectionHeader
        eyebrow="Contact"
        title={mode === "quote" ? "Get a job quote" : "Let's talk about your project"}
        subtitle="Tell us a bit about your needs. We respond in under 24 hours."
        center
      />

      <div className="mt-10 flex justify-center gap-2" data-testid="form-mode-tabs">
        <button
          data-testid="tab-contact"
          onClick={() => { setMode("contact"); setSent(false); }}
          className={`px-5 py-2 rounded-full text-sm font-semibold ${mode === "contact" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}
        >
          General contact
        </button>
        <button
          data-testid="tab-quote"
          onClick={() => { setMode("quote"); setSent(false); }}
          className={`px-5 py-2 rounded-full text-sm font-semibold ${mode === "quote" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}
        >
          Get a quote
        </button>
      </div>

      <div className="mt-12 grid lg:grid-cols-5 gap-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-2 rounded-3xl bg-slate-950 text-white p-8"
        >
          <h3 className="text-2xl font-semibold">Contact information</h3>
          <p className="mt-3 text-slate-300 text-sm">
            Reach our team directly. We're typically online 8am–7pm WAT.
          </p>
          <ul className="mt-8 space-y-5 text-sm">
            <li className="flex gap-3"><Phone className="w-5 h-5 text-blue-400" /><span>{SITE.phone}</span></li>
            <li className="flex gap-3 items-center">
              <span className="text-green-400 text-lg shrink-0">💬</span>
              <a href="https://wa.me/2348113748129" target="_blank" rel="noreferrer" className="text-green-400 hover:text-green-300 font-semibold transition">WhatsApp: +234 811 374 8129</a>
            </li>
            <li className="flex gap-3"><Mail className="w-5 h-5 text-blue-400" /><span>{SITE.email}</span></li>
            <li className="flex gap-3"><MapPin className="w-5 h-5 text-blue-400" /><span>{SITE.address}</span></li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-3 rounded-3xl border border-slate-100 bg-white p-8"
        >
          {sent ? (
            <div data-testid="form-success" className="text-center py-10">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
              <h3 className="mt-4 text-2xl font-semibold text-slate-900">Thank you!</h3>
              <p className="mt-2 text-slate-600">We've received your message and will reply soon.</p>
              <button
                onClick={() => setSent(false)}
                className="mt-6 text-blue-700 font-semibold"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
              <input name="name" required placeholder="Full name" data-testid="input-name"
                className="px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:outline-none sm:col-span-2" />
              <input name="email" type="email" required placeholder="Email" data-testid="input-email"
                className="px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:outline-none" />
              <input name="phone" placeholder="Phone (optional)" data-testid="input-phone"
                className="px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:outline-none" />
              {mode === "quote" ? (
                <>
                  <input name="company" placeholder="Company" data-testid="input-company"
                    className="px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:outline-none" />
                  <select name="service" required data-testid="input-service"
                    className="px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:outline-none">
                    <option value="">Select a service</option>
                    {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                  </select>
                  <select name="budget" data-testid="input-budget"
                    className="px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:outline-none sm:col-span-2">
                    <option value="">Estimated budget (USD)</option>
                    <option>Under 5k</option>
                    <option>5k – 20k</option>
                    <option>20k – 50k</option>
                    <option>50k – 100k</option>
                    <option>100k+</option>
                  </select>
                  <textarea name="details" required rows="5" placeholder="Tell us about your project..." data-testid="input-details"
                    className="px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:outline-none sm:col-span-2" />
                </>
              ) : (
                <>
                  <input name="subject" placeholder="Subject" data-testid="input-subject"
                    className="px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:outline-none sm:col-span-2" />
                  <textarea name="message" required rows="6" placeholder="Your message..." data-testid="input-message"
                    className="px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:outline-none sm:col-span-2" />
                </>
              )}
              {error && <p data-testid="form-error" className="text-sm text-red-600 sm:col-span-2">{error}</p>}
              <button
                type="submit"
                disabled={sending}
                data-testid="submit-btn"
                className="btn-shine sm:col-span-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-full disabled:opacity-60"
              >
                {sending ? "Sending..." : mode === "quote" ? "Request Quote" : "Send Message"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
